import {
  SecurityIdentifierType,
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import { IRepoTradeMessage } from "@/lib/interfaces and Enums/ENUMRepoTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { IRepoTicket } from "@/store/tradeFromScratchStore";
import {
  ITicketIssue,
  returnAllocationIssues,
  returnWireAllocations,
} from "./allocationRules";
import {
  ITicketField,
  returnRequiredTicketKeys,
  returnTicketFieldIssues,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// LQC-2955 - pure rules for the repo ticket. Mirrors bondOptionTicketHelper.

// RepoModelService accepts Borrow or Lend only.
export const REPO_TICKET_DIRECTIONS: TradeDirection[] = [
  TradeDirection.Borrow,
  TradeDirection.Lend,
];

export const REPO_TICKET_REQUIRED_FIELDS: ITicketField<IRepoTicket>[] = [
  { key: "isin", label: "Collateral" },
  { key: "portfolioCode", label: "Portfolio" },
  { key: "nominal", label: "Nominal", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "settlementDate", label: "Start Date", isDate: true },
  { key: "terminationDate", label: "Termination Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

export const REPO_TICKET_REQUIRED_KEYS = returnRequiredTicketKeys(
  REPO_TICKET_REQUIRED_FIELDS,
);

export function returnMissingRepoTicketEntries(ticket: IRepoTicket): ITicketIssue[] {
  return [
    ...returnTicketFieldIssues(ticket, REPO_TICKET_REQUIRED_FIELDS),
    ...returnAllocationIssues(ticket.nominal, ticket.additionalAllocations),
  ];
}

export function returnMissingRepoTicketFields(ticket: IRepoTicket): string[] {
  return returnMissingRepoTicketEntries(ticket).map(({ label }) => label);
}

// Termination on or after the start; the start on or after the trade date (spec F8).
export function returnRepoDateIssue(
  ticket: IRepoTicket,
): { key: "settlementDate" | "terminationDate"; message: string } | null {
  if (ticket.settlementDate && ticket.settlementDate < ticket.tradeDate) {
    return {
      key: "settlementDate",
      message: `Must be on or after the trade date (${ticket.tradeDate})`,
    };
  }
  if (
    ticket.terminationDate &&
    ticket.settlementDate &&
    ticket.terminationDate < ticket.settlementDate
  ) {
    return {
      key: "terminationDate",
      message: `Must be on or after the start date (${ticket.settlementDate})`,
    };
  }
  return null;
}

export function returnSentRepoTicketSummary(ticket: IRepoTicket): string {
  const nominal =
    typeof ticket.nominal === "number" && ticket.nominal > 0
      ? returnSizeFormatted(ticket.nominal)
      : "";
  return [ticket.direction, nominal, ticket.isin, "Repo"].filter(Boolean).join(" ");
}

export interface IBuildRepoTradeMessageParams {
  ticket: IRepoTicket;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

// Undefined when the ticket cannot legally be sent.
export function buildRepoTradeMessage({
  ticket,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildRepoTradeMessageParams): IRepoTradeMessage | undefined {
  if (returnMissingRepoTicketEntries(ticket).length > 0) return undefined;
  if (returnRepoDateIssue(ticket)) return undefined;
  if (!portfolio) return undefined;
  const nominal = ticket.nominal as number;

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.RepurchaseAgreement,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    // Required by the API but empty - omitting it is a 422 "Security field is required".
    security: {},
    underlyingSecurity: {
      identifierDetails: [
        { identifier: ticket.isin, identifierType: SecurityIdentifierType.ISIN },
      ],
    },
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction: ticket.direction,
      openClose: ticket.openClose,
      settlementDate: ticket.settlementDate,
      terminationDate: ticket.terminationDate,
      nominal,
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
    ),
  };
}
